import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Headless scroll controller for an infinite message thread (shared by the
 * WhatsApp, Messenger and Instagram inboxes). Owns the three behaviours that
 * make a chat thread feel right while paging keyset-style:
 *
 *  • **Auto-load older on scroll-up** — an IntersectionObserver on a top
 *    sentinel calls `fetchOlder()` as the user nears the top (no "Load older"
 *    click), gated on `hasMore && !isFetchingOlder`.
 *  • **Scroll-anchor preservation on prepend** — when an older page is
 *    prepended the viewport is re-pinned to the previously-top message, so the
 *    content the user is reading doesn't jump.
 *  • **Follow-the-tip auto-scroll** — on open (and when a new message arrives
 *    *while the user is at the bottom*) the view sticks to the newest message;
 *    a user scrolled up reading history is left in place.
 *
 * Drive it from a `useInfiniteQuery`: pass the flattened-oldest→newest list's
 * boundary ids + paging state. Attach `viewportRef` to the scroll viewport,
 * wire `onScroll` to its scroll event, and render `<div ref={topSentinelRef} />`
 * at the very top of the list.
 */
export interface ThreadScrollOptions {
  /** Total messages currently rendered (re-runs positioning on any change). */
  messageCount: number;
  /** id of the newest (last) message — drives follow-the-tip scrolling. */
  newestId: number | null;
  /** id of the oldest (first) message — drives prepend detection. */
  oldestId: number | null;
  /** Whether older messages remain (`hasNextPage`). */
  hasMore: boolean;
  /** True while an older page is in flight (`isFetchingNextPage`). */
  isFetchingOlder: boolean;
  /** Load the next older page (`fetchNextPage`). */
  fetchOlder: () => void;
  /** Switches when the open conversation changes — forces a jump to newest. */
  conversationKey: number | string | null;
}

/** Distance (px) from the bottom within which we consider the user "following"
 *  the conversation and keep auto-scrolling on new messages. */
const FOLLOW_THRESHOLD_PX = 120;

export function useThreadScroll(opts: ThreadScrollOptions) {
  const {
    messageCount,
    newestId,
    oldestId,
    hasMore,
    isFetchingOlder,
    fetchOlder,
    conversationKey,
  } = opts;

  const viewportRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);

  const nearBottomRef = useRef(true);
  const prevNewestIdRef = useRef<number | null>(null);
  const prevOldestIdRef = useRef<number | null>(null);
  const prevConvRef = useRef<number | string | null>(null);
  // Viewport metrics captured the instant we trigger a load-older, used to
  // re-anchor once the taller (prepended) content commits.
  const anchorRef = useRef<{ height: number; top: number } | null>(null);
  // Live paging state read inside the (set-up-once) observer callback.
  const stateRef = useRef({ hasMore, isFetchingOlder, fetchOlder });
  stateRef.current = { hasMore, isFetchingOlder, fetchOlder };
  const ioRef = useRef<IntersectionObserver | null>(null);

  const onScroll = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_THRESHOLD_PX;
  }, []);

  // Attach the top-sentinel observer once both refs exist (guarded so it sets
  // up exactly once even though this effect runs on every commit — the
  // viewport can mount a render after the hook first runs).
  useEffect(() => {
    const root = viewportRef.current;
    const sentinel = topSentinelRef.current;
    if (!root || !sentinel || ioRef.current) return;
    ioRef.current = new IntersectionObserver(
      (entries) => {
        const s = stateRef.current;
        if (entries[0]?.isIntersecting && s.hasMore && !s.isFetchingOlder) {
          anchorRef.current = { height: root.scrollHeight, top: root.scrollTop };
          s.fetchOlder();
        }
      },
      { root, rootMargin: '160px 0px 0px 0px', threshold: 0 },
    );
    ioRef.current.observe(sentinel);
  });

  useEffect(
    () => () => {
      ioRef.current?.disconnect();
      ioRef.current = null;
    },
    [],
  );

  // Reposition after each content change.
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    if (conversationKey !== prevConvRef.current) {
      // Opened a different conversation → jump straight to the newest message.
      el.scrollTop = el.scrollHeight;
      nearBottomRef.current = true;
    } else if (anchorRef.current && oldestId !== prevOldestIdRef.current) {
      // Older page prepended → keep the previously-top message under the cursor
      // (new height − captured height + captured top = original visual offset).
      el.scrollTop = el.scrollHeight - anchorRef.current.height + anchorRef.current.top;
      anchorRef.current = null;
    } else if (newestId !== prevNewestIdRef.current && nearBottomRef.current) {
      // New tail message while following the conversation → stay pinned.
      el.scrollTop = el.scrollHeight;
    }

    prevNewestIdRef.current = newestId;
    prevOldestIdRef.current = oldestId;
    prevConvRef.current = conversationKey;
  }, [messageCount, newestId, oldestId, conversationKey]);

  return { viewportRef, topSentinelRef, onScroll };
}
